import React from 'react'
import styled from '@emotion/styled'
import { useBetAccount } from '@mobi/account/Hooks'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, font, radius, spacing } from '@mobi/component-library/Foundations'
import { CopyField } from '@mobi/component-library/Selection&Input/CopyField'
import { usePayID } from '../../../Hooks/PayID/usePayID'
import { PayIDGenerateButton } from './PayIDGenerateButton'
import PayIDInstructions from './PayIDInstructions'

type FastWithdrawalInstructionCardProps = {
  quickPayIdEnabled?: boolean
}

const FastWithdrawalInstructionCard = ({
  quickPayIdEnabled,
}: FastWithdrawalInstructionCardProps) => {
  const { data: betAccountData } = useBetAccount()
  const accountNumber = betAccountData?.BetAccountNumber ?? 0
  const payIdDetailList = betAccountData?.PayIdDetailList ?? []
  const { activePayID, generate, status } = usePayID({
    accountNumber,
    payIdDetailList,
    location: 'Withdrawals',
  })

  return (
    <FastWithdrawalInstructionCardCard>
      <FastWithdrawalInstructionCardHeader>
        <Icon name='DuocolorZapFast' size='3.4rem' color={colors.bondi[400]} />
      </FastWithdrawalInstructionCardHeader>

      <FastWithdrawalTitle>Fast withdrawals are now available!</FastWithdrawalTitle>
      <FastWithdrawalText>
        Complete a PayID deposit successfully, and you can withdraw to the same bank account in just
        seconds.
      </FastWithdrawalText>

      {quickPayIdEnabled && (
        <>
          <SeparatorStyled />
          <QuickPayIdInstructions>
            <QuickPayIdInstruction>
              <span>1</span>
              <span>
                {activePayID ? (
                  <CopyField value={activePayID.PayId} onCopied={() => undefined} />
                ) : (
                  <PayIDGenerateButton status={status} onGenerate={generate} />
                )}
              </span>
            </QuickPayIdInstruction>
            <QuickPayIdInstruction>
              <span>2</span>
              <span>
                <div style={{ marginBottom: spacing.smx2 }}>
                  Complete a successful <strong>PayID</strong> deposit to your TABtouch account
                  (min. $5)
                </div>
                <PayIDInstructions location='Withdrawals' asTray />
              </span>
            </QuickPayIdInstruction>
          </QuickPayIdInstructions>
        </>
      )}
    </FastWithdrawalInstructionCardCard>
  )
}

const FastWithdrawalInstructionCardCard = styled.section({
  fontFamily: font.family.primary,
  backgroundColor: colors.white,
  border: `0.1rem solid ${colors.neutral[200]}`,
  borderRadius: radius.lgx2,
  padding: spacing.md,
})

const FastWithdrawalInstructionCardHeader = styled.div({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: spacing.sm,
})

const FastWithdrawalTitle = styled.h3({
  textTransform: 'none',
  backgroundColor: colors.white,
  color: colors.black,
  fontWeight: font.weight.semibold,
  margin: 0,
  padding: 0,
  ...font.size.xl,
})

const FastWithdrawalText = styled.div({
  color: colors.neutral[900],
  fontWeight: font.weight.regular,
  margin: `${spacing.smx2} 0 0`,
  ...font.size.md,
})

const SeparatorStyled = styled.hr({
  border: 'none',
  borderTop: `0.1rem solid ${colors.neutral[200]}`,
  margin: `${spacing.md} -${spacing.md} 0`,
})

const QuickPayIdInstructions = styled.ol({
  color: colors.neutral[900],
  display: 'flex',
  flexDirection: 'column',
  fontWeight: font.weight.regular,
  gap: spacing.md,
  listStyle: 'none',
  margin: 0,
  padding: `${spacing.big} 0 ${spacing.smx2}`,
  ...font.size.md,
})

const QuickPayIdInstruction = styled.li({
  alignItems: 'center',
  display: 'flex',
  gap: spacing.md,

  '& > span:last-of-type': {
    flex: 1,
  },

  '& > span:first-of-type': {
    alignItems: 'center',
    backgroundColor: colors.neutral[600],
    borderRadius: '50%',
    color: colors.white,
    display: 'inline-flex',
    flexShrink: 0,
    fontWeight: font.weight.bold,
    fontSize: '1.1rem',
    lineHeight: '1.6rem',
    height: '1.6rem',
    justifyContent: 'center',
    width: '1.6rem',
  },
})

export default FastWithdrawalInstructionCard
